import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { Body } from '~components/Drawer/src/ui/Body';
import { Footer } from '~components/Drawer/src/ui/Footer';
import { Header } from '~components/Drawer/src/ui/Header';
import { Drawer } from '~components/Drawer/src/ui/index';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';

import type { PhaseEntry } from './parsePhaseEntries';
import { parsePhaseEntries } from './parsePhaseEntries';
import type { PhaseOptions } from './types';

const CELL_SX = {
  fontSize: 13,
  py: 1,
  borderBottom: '1px solid',
  borderColor: 'divider',
};
const CELL_NOWRAP_SX = { ...CELL_SX, whiteSpace: 'nowrap' };
const ZEBRA_ROW_SX = { '&:nth-of-type(even)': { bgcolor: 'action.hover' } };

interface PhaseDrawerProps {
  onAddPhases?: (entries: { code: string; name: string }[]) => void;
  onClose: () => void;
  onRemovePhase?: (id: string) => void;
  onSetPrimary?: (id: string) => void;
  onUpdatePhase?: (id: string, updates: { code: string; name: string }) => void;
  open: boolean;
  phases: PhaseOptions[];
  readOnly?: boolean;
}

export function PhaseDrawer({
  onAddPhases,
  onClose,
  onRemovePhase,
  onSetPrimary,
  onUpdatePhase,
  open,
  phases,
  readOnly = false,
}: PhaseDrawerProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ code: '', name: '' });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [pasteValue, setPasteValue] = useState('');
  const [parsedEntries, setParsedEntries] = useState<PhaseEntry[]>([]);
  const [addedCount, setAddedCount] = useState<number | null>(null);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');

  const visiblePhases = useMemo(() => phases.filter((p) => !p._destroy), [phases]);

  const existingCodes = useMemo(
    () => new Set(visiblePhases.map((p) => p.code?.toLowerCase())),
    [visiblePhases],
  );

  const newEntries = useMemo(
    () => parsedEntries.filter((e) => !existingCodes.has(e.code.toLowerCase())),
    [parsedEntries, existingCodes],
  );

  const dupeCount = parsedEntries.length - newEntries.length;
  const hasParsed = parsedEntries.length > 0;
  const hasRows = visiblePhases.length > 0 || hasParsed;

  function startEdit(phase: PhaseOptions) {
    setEditingId(phase.id);
    setEditValues({ code: phase.code ?? '', name: phase.name });
  }

  function commitEdit(phase: PhaseOptions) {
    if (editValues.code !== (phase.code ?? '') || editValues.name !== phase.name) {
      onUpdatePhase?.(phase.id, editValues);
    }
    setEditingId(null);
  }

  function handlePaste(val: string) {
    setPasteValue(val);
    setParsedEntries(parsePhaseEntries(val));
    setAddedCount(null);
  }

  function handleAddSingle() {
    if (!newName.trim() || !newCode.trim()) return;
    onAddPhases?.([{ name: newName.trim(), code: newCode.trim() }]);
    setNewName('');
    setNewCode('');
  }

  function handleAddAll() {
    if (!newEntries.length) return;
    onAddPhases?.(newEntries);
    setAddedCount(newEntries.length);
    setPasteValue('');
    setParsedEntries([]);
  }

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        slotProps={{ paper: { sx: { width: '80%', maxWidth: 900 } } }}
      >
        <Header title={t('phases.drawer_title')}>
          <IconButton color="inherit" size="small" onClick={onClose}>
            <Close sx={{ width: 16, height: 16 }} />
          </IconButton>
        </Header>

        <Body>
          <Box sx={{ p: 2.5 }}>
            {visiblePhases.length > 0 && (
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t('phases.phase_count', { count: visiblePhases.length })}
              </Typography>
            )}

            {!hasRows && readOnly ? (
              <Box
                sx={(theme) => ({
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  fontSize: 13,
                  py: 3,
                  textAlign: 'center',
                })}
              >
                {t('phases.no_phases')}
              </Box>
            ) : (
              <TableContainer
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={(theme) => ({ bgcolor: theme.semantic.surfaceSunken })}>
                      <TableCell sx={CELL_SX}>{t('phases.name')}</TableCell>
                      <TableCell sx={CELL_NOWRAP_SX}>{t('phases.code')}</TableCell>
                      <TableCell sx={{ ...CELL_NOWRAP_SX, textAlign: 'center' }}>
                        {t('phases.primary')}
                      </TableCell>
                      {!readOnly && (
                        <TableCell sx={{ ...CELL_NOWRAP_SX, textAlign: 'center' }}>
                          {t('actions.actions')}
                        </TableCell>
                      )}
                      {hasParsed && (
                        <TableCell sx={CELL_NOWRAP_SX}>{t('common.status')}</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visiblePhases.length === 0 && parsedEntries.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={(readOnly ? 3 : 4) + (hasParsed ? 1 : 0)}
                          sx={{
                            color: 'text.disabled',
                            fontSize: 13,
                            fontStyle: 'italic',
                            py: 4,
                            textAlign: 'center',
                          }}
                        >
                          {t('phases.no_phases')}
                        </TableCell>
                      </TableRow>
                    )}
                    {visiblePhases.map((phase) => {
                      const isEditing = editingId === phase.id;
                      return (
                        <TableRow
                          key={phase.id || `inline-${phase.name}-${phase.code ?? ''}`}
                          sx={{
                            ...ZEBRA_ROW_SX,
                            ...(isEditing && { bgcolor: 'warning.lighter' }),
                          }}
                        >
                          <TableCell sx={CELL_SX}>
                            {isEditing ? (
                              <InputBase
                                value={editValues.name}
                                onChange={(e) =>
                                  setEditValues((v) => ({ ...v, name: e.target.value }))
                                }
                                size="small"
                                inputProps={{
                                  'aria-label': 'edit-name',
                                  'data-ui': 'edit-name-input',
                                }}
                                sx={{
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  fontSize: 13,
                                  px: 1,
                                  width: '100%',
                                }}
                              />
                            ) : (
                              phase.name
                            )}
                          </TableCell>
                          <TableCell sx={CELL_NOWRAP_SX}>
                            {isEditing ? (
                              <InputBase
                                value={editValues.code}
                                onChange={(e) =>
                                  setEditValues((v) => ({ ...v, code: e.target.value }))
                                }
                                size="small"
                                inputProps={{
                                  'aria-label': 'edit-code',
                                  'data-ui': 'edit-code-input',
                                }}
                                sx={{
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  fontSize: 13,
                                  px: 1,
                                }}
                              />
                            ) : (
                              phase.code
                            )}
                          </TableCell>
                          <TableCell sx={{ ...CELL_NOWRAP_SX, textAlign: 'center' }}>
                            {!readOnly ? (
                              <IconButton
                                size="small"
                                onClick={() => onSetPrimary?.(phase.id)}
                                data-ui="toggle-primary"
                                title={t('phases.primary')}
                              >
                                {phase.primary === true ? (
                                  <StarIcon
                                    // Prod-safe e2e hook for the "is primary"
                                    // assertion: MUI's dev-only
                                    // `data-testid="StarIcon"` is stripped in
                                    // staging builds. Only rendered on the
                                    // filled (primary) star.
                                    data-test-id="phase-primary-star"
                                    fontSize="small"
                                    sx={{ color: 'warning.main' }}
                                  />
                                ) : (
                                  <StarBorderIcon
                                    fontSize="small"
                                    sx={{ color: 'action.disabled' }}
                                  />
                                )}
                              </IconButton>
                            ) : phase.primary === true ? (
                              '★'
                            ) : (
                              ''
                            )}
                          </TableCell>
                          {!readOnly && (
                            <TableCell sx={{ ...CELL_NOWRAP_SX, textAlign: 'center' }}>
                              <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                                {isEditing ? (
                                  <IconButton
                                    size="small"
                                    onClick={() => commitEdit(phase)}
                                    data-ui="commit-edit"
                                    title={t('actions.save')}
                                    color="success"
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    size="small"
                                    onClick={() => startEdit(phase)}
                                    data-ui="start-edit"
                                    title={t('actions.edit')}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => setConfirmDeleteId(phase.id)}
                                  data-ui="delete-phase"
                                  title={t('actions.remove')}
                                  color="error"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          )}
                          {hasParsed && <TableCell sx={CELL_NOWRAP_SX} />}
                        </TableRow>
                      );
                    })}

                    {parsedEntries.map((entry, i) => {
                      const isDup = existingCodes.has(entry.code.toLowerCase());
                      return (
                        <TableRow key={`preview-${entry.code}-${i}`} sx={ZEBRA_ROW_SX}>
                          <TableCell sx={CELL_SX}>{entry.name}</TableCell>
                          <TableCell sx={CELL_NOWRAP_SX}>{entry.code}</TableCell>
                          <TableCell sx={CELL_NOWRAP_SX} />
                          {!readOnly && <TableCell sx={CELL_NOWRAP_SX} />}
                          <TableCell sx={CELL_NOWRAP_SX}>
                            {isDup ? (
                              <Chip
                                label={t('phases.already_exists')}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label={t('phases.new')}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!readOnly && (
                      <TableRow>
                        <TableCell sx={CELL_SX}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder={t('phases.name')}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            fullWidth
                            slotProps={{ htmlInput: { 'data-ui': 'add-name-input' } }}
                          />
                        </TableCell>
                        <TableCell sx={CELL_NOWRAP_SX}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder={t('phases.code')}
                            value={newCode}
                            onChange={(e) => setNewCode(e.target.value)}
                            fullWidth
                            slotProps={{ htmlInput: { 'data-ui': 'add-code-input' } }}
                          />
                        </TableCell>
                        <TableCell sx={CELL_NOWRAP_SX} />
                        <TableCell sx={CELL_NOWRAP_SX}>
                          <SmallButton
                            variant="contained"
                            color="primary"
                            disabled={!newName.trim() || !newCode.trim()}
                            onClick={handleAddSingle}
                            data-ui="add-single-btn"
                            startIcon={<AddIcon />}
                          >
                            {t('phases.add_phase')}
                          </SmallButton>
                        </TableCell>
                        {hasParsed && <TableCell sx={CELL_NOWRAP_SX} />}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {hasParsed && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <SmallLoadingButton
                  variant="contained"
                  color="primary"
                  onClick={handleAddAll}
                  disabled={newEntries.length === 0}
                  data-ui="add-all-btn"
                >
                  {t('phases.add_all', { count: newEntries.length })}
                </SmallLoadingButton>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('phases.preview_count', { count: parsedEntries.length })}
                  {dupeCount > 0 && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: 'warning.dark', ml: 0.75 }}
                    >
                      {' · '}
                      {t('phases.already_exist_count', { count: dupeCount })}
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}

            {!readOnly && (
              <>
                <Divider sx={{ my: 2.5 }} />

                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  {t('phases.paste_from_excel')}
                </Typography>

                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder={t('phases.paste_placeholder')}
                  value={pasteValue}
                  onChange={(e) => handlePaste(e.target.value)}
                  size="small"
                  data-ui="paste-textarea"
                  slotProps={{ htmlInput: { 'data-ui': 'paste-input' } }}
                />

                {addedCount !== null && parsedEntries.length === 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                    <Chip
                      icon={<CheckIcon />}
                      label={t('phases.added_count', { count: addedCount })}
                      color="success"
                      size="small"
                      variant="outlined"
                      data-ui="success-badge"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Body>

        <Footer>
          <SmallButton variant="outlined" color="secondary" onClick={onClose}>
            {t('actions.close')}
          </SmallButton>
        </Footer>
      </Drawer>
      <ConfirmationDialog
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => {
          if (confirmDeleteId !== null) {
            onRemovePhase?.(confirmDeleteId);
          }
          setConfirmDeleteId(null);
        }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        title={t('phases.delete_phase_title')}
      >
        <Typography>{t('phases.delete_phase_confirm')}</Typography>
      </ConfirmationDialog>
    </>
  );
}

interface PhaseBadgeProps {
  count: number;
}

export function PhaseBadge({ count }: PhaseBadgeProps) {
  if (count === 0) return null;
  return (
    <Box
      component="span"
      sx={(theme) => ({
        background: alpha(theme.palette.info.main, 0.08),
        borderRadius: 10,
        color: theme.palette.info.main,
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        ml: 0.75,
        px: 0.875,
        py: 0.125,
      })}
    >
      {count}
    </Box>
  );
}
